import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookA, Languages, Loader2, ExternalLink, Search, Sparkles } from "lucide-react";

const LANGS = ["Spanish", "French", "German", "Italian", "Portuguese", "Mandarin Chinese", "Japanese", "Arabic", "Hindi", "English"];
const SUBJECT_LABELS = {
  general: "General & Books", math: "Math", "reading-writing": "Reading & Writing",
  oceanography: "Oceanography / Science", history: "History", spanish: "Spanish", "creative-tech-art": "Creative Tech & Art",
};

export default function HelpDesk() {
  const [word, setWord] = useState("");
  const [dictLang, setDictLang] = useState("");
  const [entry, setEntry] = useState(null);
  const [dictLoading, setDictLoading] = useState(false);

  const [transText, setTransText] = useState("");
  const [transLang, setTransLang] = useState("Spanish");
  const [translation, setTranslation] = useState("");
  const [transLoading, setTransLoading] = useState(false);

  const [sites, setSites] = useState([]);

  useEffect(() => { api.get("/help-sites").then(({ data }) => setSites(data)).catch(() => {}); }, []);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!word.trim()) return toast.error("Type a word to look up.");
    setDictLoading(true); setEntry(null);
    try {
      const { data } = await api.post("/dictionary", { word, target_lang: dictLang || null });
      setEntry(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Dictionary unavailable"); }
    finally { setDictLoading(false); }
  };

  const translate = async (e) => {
    e?.preventDefault();
    if (!transText.trim()) return toast.error("Type something to translate.");
    setTransLoading(true); setTranslation("");
    try {
      const { data } = await api.post("/translate", { text: transText, target_lang: transLang });
      setTranslation(data.translation);
    } catch (err) { toast.error(err?.response?.data?.detail || "Translator unavailable"); }
    finally { setTransLoading(false); }
  };

  const grouped = sites.reduce((acc, s) => { (acc[s.subject] = acc[s.subject] || []).push(s); return acc; }, {});

  return (
    <div className="space-y-8" data-testid="helpdesk-page">
      <div>
        <div className="chip mb-2">Help & Dictionary</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Look it up yourself</h1>
        <p className="text-slate-600 mt-1">Define a word, translate it, or find extra help sites — whenever you're curious.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Dictionary */}
        <section className="p-6 rounded-3xl bg-white border border-slate-200" data-testid="dictionary-tool">
          <div className="flex items-center gap-2 mb-3"><BookA size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">Dictionary</h2></div>
          <form onSubmit={lookup} className="space-y-2">
            <div className="flex gap-2">
              <input data-testid="dict-word" value={word} onChange={(e) => setWord(e.target.value)} placeholder="Type a word or short phrase…"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200" />
              <button data-testid="dict-lookup" disabled={dictLoading} className="px-4 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold flex items-center gap-2 disabled:opacity-60">
                {dictLoading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
              </button>
            </div>
            <select data-testid="dict-lang" value={dictLang} onChange={(e) => setDictLang(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm">
              <option value="">Also translate? (optional)</option>
              {LANGS.map((l) => <option key={l} value={l}>Translate to {l}</option>)}
            </select>
          </form>
          {entry && (
            <div className="mt-4 p-4 rounded-2xl bg-sky-50 border border-sky-100" data-testid="dict-result">
              <div className="font-display text-xl font-bold text-slate-900">{entry.word} {entry.part_of_speech && <span className="text-sm font-normal text-slate-500 italic">· {entry.part_of_speech}</span>}</div>
              <div className="text-slate-800 mt-2">{entry.definition}</div>
              {entry.example && <div className="text-sm text-slate-600 mt-2 italic">"{entry.example}"</div>}
              {entry.synonyms?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">{entry.synonyms.map((s, i) => <span key={i} className="chip">{s}</span>)}</div>
              )}
              {entry.translation && (
                <div className="mt-3 pt-3 border-t border-sky-200 text-slate-800"><span className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Translation</span><div className="font-semibold">{entry.translation}</div></div>
              )}
            </div>
          )}
        </section>

        {/* Translator */}
        <section className="p-6 rounded-3xl bg-white border border-slate-200" data-testid="translate-tool">
          <div className="flex items-center gap-2 mb-3"><Languages size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">Translate</h2></div>
          <form onSubmit={translate} className="space-y-2">
            <textarea data-testid="trans-text" value={transText} onChange={(e) => setTransText(e.target.value)} rows={3} placeholder="Type a sentence or paragraph…"
              className="w-full px-4 py-3 rounded-xl border border-slate-200" />
            <div className="flex gap-2">
              <select data-testid="trans-lang" value={transLang} onChange={(e) => setTransLang(e.target.value)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm">
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <button data-testid="trans-go" disabled={transLoading} className="px-5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold flex items-center gap-2 disabled:opacity-60">
                {transLoading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
              </button>
            </div>
          </form>
          {translation && (
            <div className="mt-4 p-4 rounded-2xl bg-teal-50 border border-teal-100 text-slate-800 whitespace-pre-wrap" data-testid="trans-result">{translation}</div>
          )}
        </section>
      </div>

      {/* Extra help sites */}
      <section data-testid="help-sites">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-1">Sites for extra help</h2>
        <p className="text-slate-500 text-sm mb-4">Videos, articles, and books you can explore on your own.</p>
        <div className="space-y-6">
          {Object.keys(grouped).map((subj) => (
            <div key={subj}>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{SUBJECT_LABELS[subj] || subj}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[subj].map((s) => (
                  <a key={s.url} href={s.url} target="_blank" rel="noreferrer" data-testid={`help-site-${subj}`}
                    className="p-4 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all">
                    <div className="flex items-center justify-between">
                      <span className="chip">{s.kind}</span>
                      <ExternalLink size={14} className="text-slate-400" />
                    </div>
                    <div className="font-semibold text-slate-900 mt-2">{s.title}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.note}</div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
